import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, get, update } from "firebase/database";
import { db } from "../firebase";

// Helper for local date to avoid timezone bugs
function getLocalDateString(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Helper to calculate days ago
function getDaysAgo(dateStr, compareDateStr) {
  if (!dateStr || !compareDateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [cy, cm, cd] = compareDateStr.split("-").map(Number);
  
  const date = new Date(y, m - 1, d);
  const compareDate = new Date(cy, cm - 1, cd);
  
  const diffTime = compareDate - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

export default function PreviousEntriesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [areaId, setAreaId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState({});
  const [entries, setEntries] = useState([]);
  const [date, setDate] = useState(() => getLocalDateString());
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [lastActivityMap, setLastActivityMap] = useState({});
  
  const dataFetchedRef = useRef(false);

  // Fetch area, customers, and ALL entries for "days ago" calculation
  useEffect(() => {
    const fetchAreaData = async () => {
      try {
        const areasRef = ref(db, "areas");
        const snapshot = await get(areasRef);
        const val = snapshot.val() || {};
        
        const found = Object.entries(val).find(
          ([, v]) => v.meta && v.meta.slug === slug
        );
        
        if (found) {
          const aId = found[0];
          setAreaId(aId);
          setMeta(found[1].meta);
          setCustomers(found[1].customers || {});

          // Build the lastActivityMap from all historical entries
          const allEntries = found[1].entries || {};
          const activityMap = {};
          
          Object.entries(allEntries).forEach(([entryDate, entriesForDate]) => {
            if (!entriesForDate) return;
            Object.values(entriesForDate).forEach((e) => {
              if (!e || !e.customerId) return;
              const hasActivity = (Number(e.out) > 0) || (Number(e.in) > 0) || (Number(e.paid) > 0);
              if (hasActivity) {
                if (!activityMap[e.customerId] || entryDate > activityMap[e.customerId]) {
                  activityMap[e.customerId] = entryDate;
                }
              }
            });
          });
          setLastActivityMap(activityMap);
        }
      } catch (err) {
        console.error("Error fetching area:", err);
        setMsg("❌ Error loading data");
      }
    };
    
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      fetchAreaData();
    }
  }, [slug]);

  // Fetch entries for selected date
  const fetchEntries = useCallback(async () => {
    if (!areaId || !date) return;
    
    setLoading(true);
    try {
      const entriesRef = ref(db, `areas/${areaId}/entries/${date}`);
      const snapshot = await get(entriesRef);
      const val = snapshot.val() || {};
      const arr = Object.entries(val).map(([id, data]) => ({ id, ...data }));
      setEntries(arr);
    } catch (err) {
      console.error("Error fetching entries:", err);
      setMsg("❌ Error loading entries");
    } finally {
      setLoading(false);
    }
  }, [areaId, date]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const startEdit = (entry) => {
    setEditing(entry.id);
    setEditValues({
      out: entry.out,
      in: entry.in,
      paid: entry.paid,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValues({});
  };

  const saveEdit = async (entryId) => {
    if (!areaId || !date) return;
    setUpdating(true);
    
    try {
      const oldEntry = entries.find((e) => e.id === entryId);
      if (!oldEntry) {
        setMsg("❌ Original entry not found.");
        setUpdating(false);
        return;
      }

      const newOut = Number(editValues.out) || 0;
      const newIn = Number(editValues.in) || 0;
      const newPaid = Number(editValues.paid) || 0;
      
      const oldOut = Number(oldEntry.out) || 0;
      const oldIn = Number(oldEntry.in) || 0;
      const oldPaid = Number(oldEntry.paid) || 0;

      const deltaPaid = newPaid - oldPaid;
      const deltaJars = (newOut - newIn) - (oldOut - oldIn); // ✅ FIX: Also track jar changes
      
      const custId = oldEntry.customerId;
      const updates = {};
      
      // Update entry
      updates[`areas/${areaId}/entries/${date}/${entryId}`] = {
        ...oldEntry,
        out: newOut,
        in: newIn,
        paid: newPaid,
        timestamp: Date.now()
      };

      // Update customer balances if customer exists
      if (custId && customers[custId]) {
        const custRef = ref(db, `areas/${areaId}/customers/${custId}`);
        const custSnap = await get(custRef);
        const custVal = custSnap.exists() ? custSnap.val() : {};
        
        let pendingAmount = Number(custVal.pendingAmount || custVal.pending || 0);
        let advance = Number(custVal.advance || custVal.advanceAmount || 0);
        let pendingJars = Number(custVal.pendingJars || 0);

        // ✅ FIX: Update Pending Jars
        pendingJars = Math.max(0, pendingJars + deltaJars);
        
        // Update Pending Amount & Advance based on deltaPaid
        if (deltaPaid > 0) {
          let remaining = deltaPaid;
          if (pendingAmount > 0) {
            const reduceAmount = Math.min(pendingAmount, remaining);
            pendingAmount = Number((pendingAmount - reduceAmount).toFixed(2));
            remaining = Number((remaining - reduceAmount).toFixed(2));
          }
          if (remaining > 0) {
            advance = Number((advance + remaining).toFixed(2));
          }
        } else if (deltaPaid < 0) {
          let deficit = Math.abs(deltaPaid);
          if (advance > 0) {
            const reduceAmount = Math.min(advance, deficit);
            advance = Number((advance - reduceAmount).toFixed(2));
            deficit = Number((deficit - reduceAmount).toFixed(2));
          }
          if (deficit > 0) {
            pendingAmount = Number((pendingAmount + deficit).toFixed(2));
          }
        }
        
        updates[`areas/${areaId}/customers/${custId}/pendingJars`] = pendingJars;
        updates[`areas/${areaId}/customers/${custId}/pendingAmount`] = pendingAmount;
        updates[`areas/${areaId}/customers/${custId}/advance`] = advance;
      }
      
      await update(ref(db), updates);
      
      // Update local state
      setEntries(prev => prev.map(e => 
        e.id === entryId ? { ...e, out: newOut, in: newIn, paid: newPaid } : e
      ));
      
      setMsg("✅ Entry updated successfully!");
      setEditing(null);
      setTimeout(() => setMsg(""), 2000);
    } catch (err) {
      console.error(err);
      setMsg("❌ Error updating entry.");
    } finally {
      setUpdating(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    
    setUpdating(true);
    try {
      const oldEntry = entries.find((e) => e.id === entryId);
      if (!oldEntry) {
        setMsg("❌ Entry not found.");
        setUpdating(false);
        return;
      }
      
      const updates = {};
      updates[`areas/${areaId}/entries/${date}/${entryId}`] = null;
      
      // Reverse the effects on customer balance
      if (oldEntry.customerId && customers[oldEntry.customerId]) {
        const custRef = ref(db, `areas/${areaId}/customers/${oldEntry.customerId}`);
        const custSnap = await get(custRef);
        const custVal = custSnap.exists() ? custSnap.val() : {};
        
        let pendingAmount = Number(custVal.pendingAmount || custVal.pending || 0);
        let advance = Number(custVal.advance || custVal.advanceAmount || 0);
        let pendingJars = Number(custVal.pendingJars || 0);

        const oldPaid = Number(oldEntry.paid) || 0;
        const oldOut = Number(oldEntry.out) || 0;
        const oldIn = Number(oldEntry.in) || 0;
        const price = Number(oldEntry.price) || 0;
        const expectedAmount = oldOut * price;
        
        // ✅ FIX: Reverse Pending Jars
        const netJars = oldOut - oldIn;
        pendingJars = Math.max(0, pendingJars - netJars);

        // Reverse the payment effect
        if (oldPaid >= expectedAmount) {
          advance = Math.max(0, advance - (oldPaid - expectedAmount));
        } else {
          pendingAmount = Math.max(0, pendingAmount - (expectedAmount - oldPaid));
        }
        
        updates[`areas/${areaId}/customers/${oldEntry.customerId}/pendingJars`] = pendingJars;
        updates[`areas/${areaId}/customers/${oldEntry.customerId}/pendingAmount`] = pendingAmount;
        updates[`areas/${areaId}/customers/${oldEntry.customerId}/advance`] = advance;
      }
      
      await update(ref(db), updates);
      
      // Update local state
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setMsg("✅ Entry deleted successfully!");
      setTimeout(() => setMsg(""), 2000);
    } catch (err) {
      console.error(err);
      setMsg("❌ Error deleting entry.");
    } finally {
      setUpdating(false);
    }
  };

  const totalOut = entries.reduce((a, e) => a + (Number(e.out) || 0), 0);
  const totalIn = entries.reduce((a, e) => a + (Number(e.in) || 0), 0);
  const totalPaid = entries.reduce((a, e) => a + (Number(e.paid) || 0), 0);

  return (
    <div className="prev-entries-container">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h2>Previous Entries</h2>
        <div className="header-spacer"></div>
      </div>

      {meta && <div className="area-name">{meta.name}</div>}

      {/* Date Selector */}
      <div className="date-selector">
        <label>📅 Select Date:</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="date-input"
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading entries...</p>
        </div>
      )}

      {/* Entries List */}
      {!loading && (
        <>
          {/* Desktop Table View */}
          <div className="desktop-view">
            <div className="entries-table">
              <div className="table-header">
                <div>Customer</div>
                <div>Type</div>
                <div>Out</div>
                <div>In</div>
                <div>Net</div>
                <div>Price</div>
                <div>Paid (₹)</div>
                <div>Expected (₹)</div>
                <div>Actions</div>
              </div>

              {entries.length === 0 ? (
                <div className="no-entries">
                  <div className="empty-icon">📭</div>
                  <p>No entries found for {date}</p>
                </div>
              ) : (
                entries.map((entry) => {
                  const cust = customers[entry.customerId] || {};
                  const isEditing = editing === entry.id;
                  const netJars = (entry.out || 0) - (entry.in || 0);
                  const expected = (entry.out || 0) * (entry.price || 0);
                  const diff = (entry.paid || 0) - expected;
                  
                  // ✅ Calculate days ago
                  const lastDate = lastActivityMap[entry.customerId];
                  const daysAgo = lastDate ? getDaysAgo(lastDate, date) : null;

                  return (
                    <div key={entry.id} className={`table-row ${isEditing ? 'editing' : ''}`}>
                      <div className="customer-cell">
                        <strong>{cust.name || "Unknown"}</strong>
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                          <span className={`type-badge ${cust.type}`}>
                            {cust.type === 'cooljar' ? '🥤 Cool Jar' : '🏺 Jar'}
                          </span>
                          {daysAgo > 0 && (
                            <span className={`type-badge ${daysAgo >= 2 ? 'warning' : ''}`} style={{ background: daysAgo >= 2 ? '#f8d7da' : '#e2e3e5', color: daysAgo >= 2 ? '#721c24' : '#495057' }}>
                              🕒 {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {isEditing ? (
                        <>
                          <div>
                            <input
                              type="number"
                              value={editValues.out}
                              onChange={(e) => setEditValues({ ...editValues, out: e.target.value })}
                              className="edit-input"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              value={editValues.in}
                              onChange={(e) => setEditValues({ ...editValues, in: e.target.value })}
                              className="edit-input"
                            />
                          </div>
                          <div className="net-cell">
                            {(Number(editValues.out) - Number(editValues.in))}
                          </div>
                          <div>₹{entry.price}</div>
                          <div>
                            <input
                              type="number"
                              value={editValues.paid}
                              onChange={(e) => setEditValues({ ...editValues, paid: e.target.value })}
                              className="edit-input"
                            />
                          </div>
                          <div>
                            ₹{((Number(editValues.out) || 0) * (entry.price || 0)).toFixed(2)}
                          </div>
                          <div className="actions">
                            <button onClick={() => saveEdit(entry.id)} className="save-btn" disabled={updating}>
                              💾 Save
                            </button>
                            <button onClick={cancelEdit} className="cancel-btn">
                              ✕ Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>{entry.out || 0}</div>
                          <div>{entry.in || 0}</div>
                          <div className={netJars > 0 ? 'positive' : netJars < 0 ? 'negative' : ''}>
                            {netJars}
                          </div>
                          <div>₹{entry.price}</div>
                          <div className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : ''}>
                            ₹{entry.paid}
                          </div>
                          <div className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : ''}>
                            ₹{expected.toFixed(2)}
                          </div>
                          <div className="actions">
                            <button onClick={() => startEdit(entry)} className="edit-btn">
                              ✏️ Edit
                            </button>
                            <button onClick={() => deleteEntry(entry.id)} className="delete-btn">
                              🗑️ Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-view">
            {entries.length === 0 ? (
              <div className="no-entries">
                <div className="empty-icon">📭</div>
                <p>No entries found for {date}</p>
              </div>
            ) : (
              entries.map((entry) => {
                const cust = customers[entry.customerId] || {};
                const isEditing = editing === entry.id;
                const netJars = (entry.out || 0) - (entry.in || 0);
                const expected = (entry.out || 0) * (entry.price || 0);
                const diff = (entry.paid || 0) - expected;
                
                const lastDate = lastActivityMap[entry.customerId];
                const daysAgo = lastDate ? getDaysAgo(lastDate, date) : null;

                return (
                  <div key={entry.id} className="entry-card">
                    <div className="card-header">
                      <div className="customer-info">
                        <h3>{cust.name || "Unknown"}</h3>
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                          <span className={`type-badge ${cust.type}`}>
                            {cust.type === 'cooljar' ? '🥤 Cool Jar' : '🏺 Jar'}
                          </span>
                          {daysAgo > 0 && (
                            <span className={`type-badge ${daysAgo >= 2 ? 'warning' : ''}`} style={{ background: daysAgo >= 2 ? '#f8d7da' : '#e2e3e5', color: daysAgo >= 2 ? '#721c24' : '#495057' }}>
                              🕒 {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                            </span>
                          )}
                        </div>
                      </div>
                      {!isEditing && (
                        <div className="card-actions">
                          <button onClick={() => startEdit(entry)} className="edit-btn-mobile">
                            ✏️
                          </button>
                          <button onClick={() => deleteEntry(entry.id)} className="delete-btn-mobile">
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {isEditing ? (
                      <div className="edit-form">
                        <div className="edit-field">
                          <label>Jars Out:</label>
                          <input
                            type="number"
                            value={editValues.out}
                            onChange={(e) => setEditValues({ ...editValues, out: e.target.value })}
                          />
                        </div>
                        <div className="edit-field">
                          <label>Jars In:</label>
                          <input
                            type="number"
                            value={editValues.in}
                            onChange={(e) => setEditValues({ ...editValues, in: e.target.value })}
                          />
                        </div>
                        <div className="edit-field">
                          <label>Amount Paid:</label>
                          <input
                            type="number"
                            value={editValues.paid}
                            onChange={(e) => setEditValues({ ...editValues, paid: e.target.value })}
                          />
                        </div>
                        <div className="edit-actions">
                          <button onClick={() => saveEdit(entry.id)} className="save-btn" disabled={updating}>
                            Save Changes
                          </button>
                          <button onClick={cancelEdit} className="cancel-btn">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="card-details">
                        <div className="detail-row">
                          <span>📤 Out:</span>
                          <strong>{entry.out || 0}</strong>
                        </div>
                        <div className="detail-row">
                          <span>📥 In:</span>
                          <strong>{entry.in || 0}</strong>
                        </div>
                        <div className="detail-row">
                          <span>🔄 Net:</span>
                          <strong className={netJars > 0 ? 'positive' : netJars < 0 ? 'negative' : ''}>
                            {netJars}
                          </strong>
                        </div>
                        <div className="detail-row">
                          <span>💰 Price/Jar:</span>
                          <strong>₹{entry.price}</strong>
                        </div>
                        <div className="detail-row">
                          <span>💵 Paid:</span>
                          <strong className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : ''}>
                            ₹{entry.paid}
                          </strong>
                        </div>
                        <div className="detail-row">
                          <span>📊 Expected:</span>
                          <strong className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : ''}>
                            ₹{expected.toFixed(2)}
                          </strong>
                        </div>
                        <div className="detail-row">
                          <span>⚖️ Difference:</span>
                          <strong className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : ''}>
                            {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                          </strong>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Totals Bar */}
          {entries.length > 0 && (
            <div className="totals-bar">
              <div className="total-item">
                <span>📤 Total Out:</span>
                <strong>{totalOut}</strong>
              </div>
              <div className="total-item">
                <span>📥 Total In:</span>
                <strong>{totalIn}</strong>
              </div>
              <div className="total-item">
                <span>🔄 Net Jars:</span>
                <strong className={totalOut - totalIn > 0 ? 'positive' : 'negative'}>
                  {totalOut - totalIn}
                </strong>
              </div>
              <div className="total-item">
                <span>💰 Total Paid:</span>
                <strong>₹{totalPaid}</strong>
              </div>
            </div>
          )}
        </>
      )}

      {/* Message Toast */}
      {msg && (
        <div className={`message-toast ${msg.includes('✅') ? 'success' : 'error'}`}>
          {msg}
        </div>
      )}

      <style jsx>{`
        /* Keep all your existing CSS exactly as it is */
        .prev-entries-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .page-header h2 {
          color: white;
          margin: 0;
          font-size: 20px;
        }
        .back-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
        }
        .header-spacer { width: 60px; }
        .area-name {
          text-align: center;
          color: white;
          font-size: 14px;
          margin-bottom: 20px;
          opacity: 0.9;
        }
        .date-selector {
          background: rgba(255,255,255,0.95);
          padding: 15px;
          border-radius: 12px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
        }
        .date-selector label { font-weight: 600; color: #333; }
        .date-input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }
        .desktop-view {
          display: block;
          background: rgba(255,255,255,0.95);
          border-radius: 12px;
          overflow-x: auto;
        }
        .entries-table { min-width: 800px; }
        .table-header {
          display: grid;
          grid-template-columns: 150px 80px 60px 60px 60px 60px 100px 100px 100px;
          background: #f8f9fa;
          padding: 12px;
          font-weight: 600;
          border-bottom: 2px solid #e0e0e0;
          position: sticky;
          top: 0;
        }
        .table-row {
          display: grid;
          grid-template-columns: 150px 80px 60px 60px 60px 60px 100px 100px 100px;
          padding: 12px;
          border-bottom: 1px solid #f0f0f0;
          align-items: center;
        }
        .table-row:hover { background: #f8f9fa; }
        .table-row.editing { background: #fff3cd; }
        .customer-cell {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .type-badge {
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 12px;
          display: inline-block;
          width: fit-content;
        }
        .type-badge.jar { background: #e3f2fd; color: #1976d2; }
        .type-badge.cooljar { background: #e8f5e9; color: #388e3c; }
        .edit-input {
          width: 100%;
          padding: 4px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        .actions { display: flex; gap: 8px; }
        .edit-btn, .save-btn, .cancel-btn, .delete-btn {
          padding: 4px 8px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }
        .edit-btn { background: #ffc107; color: #333; }
        .save-btn { background: #28a745; color: white; }
        .cancel-btn { background: #6c757d; color: white; }
        .delete-btn { background: #dc3545; color: white; }
        .positive { color: #28a745; font-weight: 600; }
        .negative { color: #dc3545; font-weight: 600; }
        .mobile-view { display: none; }
        .entry-card {
          background: rgba(255,255,255,0.95);
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 12px;
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e0e0e0;
        }
        .customer-info h3 { margin: 0 0 5px 0; font-size: 16px; }
        .card-actions { display: flex; gap: 8px; }
        .edit-btn-mobile, .delete-btn-mobile {
          padding: 6px 10px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }
        .edit-btn-mobile { background: #ffc107; }
        .delete-btn-mobile { background: #dc3545; color: white; }
        .card-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
        }
        .edit-form { display: flex; flex-direction: column; gap: 12px; }
        .edit-field {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }
        .edit-field label { font-size: 13px; font-weight: 600; }
        .edit-field input {
          flex: 1;
          padding: 6px;
          border: 1px solid #ddd;
          border-radius: 6px;
        }
        .edit-actions { display: flex; gap: 10px; margin-top: 5px; }
        .edit-actions button {
          flex: 1;
          padding: 8px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        .totals-bar {
          background: rgba(255,255,255,0.95);
          border-radius: 12px;
          padding: 15px;
          margin-top: 20px;
          display: flex;
          justify-content: space-around;
          flex-wrap: wrap;
          gap: 15px;
        }
        .total-item { display: flex; gap: 8px; font-size: 14px; }
        .total-item strong { color: #667eea; }
        .no-entries { text-align: center; padding: 60px 20px; }
        .empty-icon { font-size: 48px; margin-bottom: 10px; }
        .loading-state {
          background: rgba(255,255,255,0.95);
          border-radius: 12px;
          padding: 40px;
          text-align: center;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .message-toast {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 12px 24px;
          border-radius: 8px;
          color: white;
          font-weight: 600;
          z-index: 1000;
          animation: slideUp 0.3s ease;
        }
        .message-toast.success { background: #28a745; }
        .message-toast.error { background: #dc3545; }
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @media (max-width: 768px) {
          .prev-entries-container { padding: 10px; }
          .page-header h2 { font-size: 16px; }
          .desktop-view { display: none; }
          .mobile-view { display: block; }
          .date-selector { flex-direction: column; align-items: stretch; }
          .totals-bar { flex-direction: column; gap: 8px; }
          .total-item { justify-content: space-between; }
        }
      `}</style>
    </div>
  );
}